import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Award, Plus, Trash2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function SchoolPlacements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ year: new Date().getFullYear(), exam_type: "LGS", total_applicants: "", placed_count: "", scholarship_avg_pct: "", source_note: "" });

  const load = () => api.get(`${V1}/school/placements/mine`).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post(`${V1}/school/placements`, {
        ...form,
        year: parseInt(form.year),
        total_applicants: parseInt(form.total_applicants) || 0,
        placed_count: parseInt(form.placed_count) || 0,
        scholarship_avg_pct: form.scholarship_avg_pct ? parseFloat(form.scholarship_avg_pct) : null,
      });
      toast.success("Kayıt gönderildi — admin onayı bekleniyor");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const del = async (id) => {
    if (!window.confirm("Kayıt silinsin mi?")) return;
    await api.delete(`${V1}/school/placements/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="school-placements-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-accent">Şeffaflık</div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Award className="h-7 w-7 text-primary" /> Yerleştirme Verileri</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Geçmiş yıllara ait yerleştirme/burs istatistiklerinizi gönderin. Admin onayından geçen
            kayıtlar okul profilinizde ailelere gösterilir (güven artırıcı, doğrulanmış veri).
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-placement-btn"><Plus className="h-4 w-4" /> Yeni Kayıt</Button></DialogTrigger>
          <DialogContent className="max-w-md" data-testid="placement-dialog">
            <DialogHeader><DialogTitle>Yerleştirme Verisi Ekle</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Yıl</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="placement-year" /></div>
                <div><Label>Sınav Türü</Label>
                  <Select value={form.exam_type} onValueChange={(v) => setForm({ ...form, exam_type: v })}>
                    <SelectTrigger data-testid="placement-exam-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="LGS">LGS</SelectItem><SelectItem value="TYT">TYT</SelectItem><SelectItem value="AYT">AYT</SelectItem><SelectItem value="GENEL">Genel</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Toplam Başvuran</Label><Input type="number" value={form.total_applicants} onChange={(e) => setForm({ ...form, total_applicants: e.target.value })} data-testid="placement-applicants" /></div>
                <div><Label>Yerleşen Sayısı</Label><Input type="number" value={form.placed_count} onChange={(e) => setForm({ ...form, placed_count: e.target.value })} data-testid="placement-placed" /></div>
              </div>
              <div><Label>Ortalama Burs Oranı (%) — opsiyonel</Label><Input type="number" value={form.scholarship_avg_pct} onChange={(e) => setForm({ ...form, scholarship_avg_pct: e.target.value })} data-testid="placement-scholarship-pct" /></div>
              <div><Label>Kaynak Notu — opsiyonel</Label><Input value={form.source_note} onChange={(e) => setForm({ ...form, source_note: e.target.value })} placeholder="ör. İl Milli Eğitim onaylı liste" data-testid="placement-source" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button onClick={submit} data-testid="submit-placement-btn">Gönder</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left p-3">Yıl</th><th className="text-left p-3">Sınav</th>
              <th className="text-left p-3">Başvuran</th><th className="text-left p-3">Yerleşen</th>
              <th className="text-left p-3">Burs Ort.</th><th className="text-left p-3">Durum</th><th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">Henüz kayıt yok.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-border" data-testid={`placement-row-${p.id}`}>
                <td className="p-3 font-mono">{p.year}</td>
                <td className="p-3">{p.exam_type}</td>
                <td className="p-3">{p.total_applicants}</td>
                <td className="p-3 font-bold">{p.placed_count}</td>
                <td className="p-3">{p.scholarship_avg_pct != null ? `%${p.scholarship_avg_pct}` : "-"}</td>
                <td className="p-3">
                  {p.verified ? (
                    <span className="inline-flex items-center gap-1 text-xs text-success"><CheckCircle2 className="h-3.5 w-3.5" /> Onaylı</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Onay Bekliyor</span>
                  )}
                </td>
                <td className="p-3">
                  <button onClick={() => del(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-placement-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
